/// <amd-module name="Wit/QueriesHub/ResultsView/ImportWorkItemsPanel" />
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/QueriesHub/Resources";
import { IQueryDisplayColumn, IQueryResult } from "../Common/Interfaces";
import { IWorkItemsImportData, QueryResultCSVImporter } from "./QueryResultCSVImporter";

export interface IImportWorkItemsPanelProps {
    /*
     * Callback called when panel is dismissed by either clicking Cancel or hitting Esc
     */
    onDismiss: () => void;

    /*
     * Callback called when user clicks Import button in the panel
     */
    onImportClick?: (queryResultModel: IQueryResult | undefined) => void;
}

export class ImportWorkItemsPanel extends VssComponent<IImportWorkItemsPanelProps> {
    private _chooseFileRef = React.createRef<Button>();
    private _container: JSX.Element;
    private _csvImportData = new ObservableValue<IWorkItemsImportData | undefined>(undefined);
    private _csvString = new ObservableValue<string | undefined>(undefined);
    private _fileInputRef = React.createRef<HTMLInputElement>();
    private _csvFile = new ObservableValue<File | undefined>(undefined);
    private _errorMessage = new ObservableValue<string | undefined>(undefined);
    private _isLoading = new ObservableValue<boolean>(false);
    private _percentComplete = new ObservableValue<number>(0);
    private _queryResultCSVImporter: QueryResultCSVImporter;

    constructor(props: IImportWorkItemsPanelProps, context: IVssComponentContext) {
        super(props, context);
        const pageData = getTFSPageData(this.context.pageContext);
        if (pageData?.project?.id) {
            this._queryResultCSVImporter = new QueryResultCSVImporter(
                pageData?.project?.id,
                this.context.pageContext,
                this.updatePercentComplete.bind(this)
            );
        } else {
            throw Resources.ProjectNotFoundError;
        }

        const button = React.createElement("input", {
            type: "file",
            accept: ".csv",
            ref: this._fileInputRef,
            style: { display: "none" },
            onChange: e => {
                this._filterFilesAndUpdateState(e.target.files);
            }
        });

        this._container = (
            <Observer csvFile={this._csvFile}>
                {(observableProps: { csvFile: File | undefined }) => {
                    return (
                        <div>
                            <Button text={Resources.ChooseFile} onClick={this._inputAction} ref={this._chooseFileRef} />
                            {button}
                            <span className={"margin-left-8"}>{observableProps.csvFile?.name}</span>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    public static componentType = "importCSVPanel";

    public render(): JSX.Element {
        return (
            <Observer csvFile={this._csvFile}>
                {(observableProps: { csvFile: File | undefined }) => {
                    return (
                        <Panel
                            className={"margin-left-16"}
                            onDismiss={this.props.onDismiss}
                            titleProps={{ className: "margin-top-16", text: Resources.ImportWorkItems }}
                            lightDismiss={false}
                            footerButtonProps={[
                                {
                                    ariaLabel: Resources.Import,
                                    disabled: !observableProps.csvFile,
                                    onClick: this._onImportClick,
                                    primary: true,
                                    style: { marginRight: "8px" },
                                    text: Resources.Import
                                },
                                {
                                    ariaLabel: Resources.Cancel,
                                    onClick: this.props.onDismiss,
                                    text: Resources.Cancel
                                }
                            ]}
                        >
                            {this._renderContents()}
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    public updatePercentComplete(newPercentComplete: number): void {
        this.setState({ percentComplete: newPercentComplete });
    }

    private _renderContents(): React.ReactNode {
        return (
            <div className="flex-grow import-panel">
                <Observer isLoading={this._isLoading} percentComplete={this._percentComplete}>
                    {(observableProps: { isLoading: boolean; percentComplete: number }) => {
                        const percentCompleteMessage = `${String(Math.floor(observableProps.percentComplete * 100))}%`;
                        return observableProps.isLoading ? (
                            <div>
                                <div>{Resources.ImportCSVPanelLoadingText}</div>
                                <Spinner className="margin-top-16" size={SpinnerSize.large} label={percentCompleteMessage} />
                            </div>
                        ) : (
                            <div className="margin-bottom-16">
                                <div className="margin-bottom-16">
                                    {Resources.ImportCSVPanelText}
                                    <br />
                                    <Link href="https://go.microsoft.com/fwlink/?linkid=2155935" target="_blank">
                                        {Resources.LearnMoreAboutImportLink}
                                    </Link>
                                </div>
                                {this._container}
                            </div>
                        );
                    }}
                </Observer>
                <Observer errorMessage={this._errorMessage}>
                    {(observableProps: { errorMessage: string | undefined }) => {
                        return observableProps.errorMessage != undefined ? (
                            <div className="margin-right-16">
                                <div className="margin-bottom-4">{Resources.ImportCSVErrorHeader}</div>
                                <MessageBar className="import-work-items-error-message" severity={MessageBarSeverity.Error}>
                                    {observableProps.errorMessage}
                                </MessageBar>
                            </div>
                        ) : null;
                    }}
                </Observer>
            </div>
        );
    }

    private _filterFilesAndUpdateState(files: FileList | null): void {
        this._errorMessage.value = undefined;
        const reader = new FileReader();
        // Filter for 1st instance of CSV file
        if (files) {
            for (let i = 0; i < files.length; i++) {
                const extension = files[i].name.split(".").pop();
                if (extension === "csv") {
                    this._csvFile.value = files[i];
                    break;
                }
            }
        }
        if (this._csvFile.value == null) {
            this._errorMessage.value = Resources.InvalidCSVFileFormat;
            return;
        }
        reader.onload = () => {
            const result: string | undefined = reader.result?.toString();
            this._csvString.value = result;
        };
        reader.readAsText(this._csvFile.value);
    }

    private _inputAction = (): void => {
        this._fileInputRef.current?.click();
    };

    protected _onImportClick = async () => {
        this._errorMessage.value = undefined;
        this._isLoading.value = true;

        try {
            const importData = await this._queryResultCSVImporter.importCSVFile(this.context.pageContext, this._csvString.value);

            if (!importData) {
                this._csvImportData.value = undefined;
                this._isLoading.value = false;
                this._percentComplete.value = 0;
                return;
            }
            this._csvImportData.value = importData;
            this._percentComplete.value = 100;
            this._isLoading.value = false;
            this._csvFile.value = undefined;

            const queryResultModel = createQueryResultModel(this._csvImportData.value);
            if (this.props.onImportClick) {
                this.props.onImportClick(queryResultModel);
            }

            this.props.onDismiss();
        } catch (error) {
            if (typeof error === "string") {
                this._errorMessage.value = error;
            } else if (error instanceof Error) {
                this._errorMessage.value = error.message;
            }
            this._csvImportData.value = undefined;
            this._csvString.value = undefined;
            this._csvFile.value = undefined;
            this._isLoading.value = false;
            this._percentComplete.value = 0;
        }
    };
}

function createQueryResultModel(importData: IWorkItemsImportData): IQueryResult | undefined {
    const mandatoryPayloadFields = [CoreFieldRefNames.Rev, CoreFieldRefNames.TeamProject];
    const mandatoryFilterFields = [CoreFieldRefNames.AssignedTo, CoreFieldRefNames.State, CoreFieldRefNames.Tags];
    if (!importData) {
        return;
    }
    const columnOptionsFields = importData.fieldColumns;
    const columns = mandatoryPayloadFields.concat(columnOptionsFields.map(field => field.referenceName));
    mandatoryFilterFields.forEach(field => {
        if (columns.indexOf(field) == -1) {
            columns.push(field);
        }
    });

    const displayColumns: IQueryDisplayColumn[] = [];
    columnOptionsFields.forEach(field => {
        displayColumns.push({
            name: field.referenceName,
            text: field.name,
            fieldId: field.id,
            isIdentity: field.isIdentity,
            width: 150
        });
    });

    importData.columns.push("isValid");
    importData.fieldValues.forEach((row, index) => {
        row.push(importData.workItems[index].isValid().toString());
    });

    const payload = {
        columns: importData.columns,
        rows: importData.fieldValues
    };

    const result: IQueryResult = {
        targetIds: importData.targetIds,
        isLinkQuery: !importData.isTreeQuery,
        isTreeQuery: importData.isTreeQuery,
        pageColumns: columns,
        queryRan: false,
        wiql: "",
        columns: displayColumns,
        sortColumns: [],
        payload: payload,
        sourceIds: importData.sourceIds,
        linkIds: [],
        workItems: importData.workItems
    };

    return result;
}
